import { useEffect, useRef, useState } from 'react';

import type { PlayerPlayP2pClient } from '@genially/p2p-client';
import { PlayerPlayP2pClientEventType } from '@genially/p2p-client';

type HostCursorPosition = {
  xRatio: number;
  yRatio: number;
  slideAspectRatio: number;
} | null;

const HIDE_TIMEOUT_MS = 500;

/**
 * Tracks the host cursor position received over P2P.
 * The position is automatically cleared after HIDE_TIMEOUT_MS of inactivity.
 */
export function useHostCursorPosition(client: PlayerPlayP2pClient): HostCursorPosition {
  const [hostCursorPosition, setHostCursorPosition] = useState<HostCursorPosition>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleHostCursorPositionReceived = ({
      xRatio,
      yRatio,
      slideAspectRatio,
    }: {
      xRatio: number;
      yRatio: number;
      slideAspectRatio: number;
    }) => {
      setHostCursorPosition({ xRatio, yRatio, slideAspectRatio });
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => {
        setHostCursorPosition(null);
        hideTimerRef.current = null;
      }, HIDE_TIMEOUT_MS);
    };

    client.on(
      PlayerPlayP2pClientEventType.HostCursorPositionReceived,
      handleHostCursorPositionReceived,
    );

    return () => {
      client.off(
        PlayerPlayP2pClientEventType.HostCursorPositionReceived,
        handleHostCursorPositionReceived,
      );
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [client]);

  return hostCursorPosition;
}
